import { useState } from "react";
import { Button, Dropdown, Modal, Tooltip } from "antd";
import { CheckCircleOutlined, DownOutlined, FolderOpenOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { postData } from "../../api/apiService";
import { NOTICES } from "../../api/endpointMap";

// Reusable "Change Status" action for the portal notice lists (Income Tax,
// GST, TDS). Sits next to "Bulk Delete" and works on the same selection: the
// user ticks notices, picks Open or Close from this dropdown, confirms, and
// every ticked notice is updated in one request.
//
// It lives here, like DeleteAllNoticesButton, so the status logic exists in
// ONE place instead of being copy-pasted into four list pages. Each page only
// passes what differs for it:
//   selectedIds - the ticked row ids (the page's existing selection state)
//   doctype     - the doctype string the page already sends to its
//                 delete-documents call (E Proceeding / Response to
//                 Outstanding Demand / GST Notice And Order /
//                 GST Additional Notice And Order / Quarter), or a function
//                 returning it when it depends on the active tab
//   onDone      - the page's list refresh (same one the delete uses)
//
// The backend (POST /notices/update-status) reports an honest partial
// result: rows outside the user's client scope come back in `skipped`, and
// the toast says so rather than claiming everything changed.
const STATUSES = [
  { key: "Open", label: "Mark as Open", icon: <FolderOpenOutlined /> },
  { key: "Close", label: "Mark as Close", icon: <CheckCircleOutlined /> },
];

// Illustration for the confirm popup, drawn inline so there is nothing to
// download: it paints in the same frame as the modal and stays crisp at any
// size (the delete popup's PNG is 120 KB; this is under 1 KB). It follows the
// chosen status so the picture says the same thing as the text: an open
// folder for Open, a completed tick for Close.
function StatusArt({ status, size = 104 }) {
  const isClose = status === "Close";
  const tint = isClose ? "#16a34a" : "#3656b6";
  const soft = isClose ? "#dcfce7" : "#e8edfb";
  return (
    <svg width={size} height={size} viewBox="0 0 104 104" aria-hidden="true" focusable="false">
      <circle cx="52" cy="52" r="50" fill={soft} />
      {isClose ? (
        <>
          {/* document with a tick seal */}
          <rect x="30" y="22" width="40" height="52" rx="5" fill="#fff" stroke={tint} strokeWidth="3" />
          <line x1="38" y1="36" x2="62" y2="36" stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
          <line x1="38" y1="46" x2="62" y2="46" stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
          <line x1="38" y1="56" x2="52" y2="56" stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
          <circle cx="68" cy="68" r="15" fill={tint} />
          <polyline points="60,68 66,74 77,62" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          {/* open folder with a sheet inside */}
          <path d="M24 38 h20 l6 6 h30 v30 a4 4 0 0 1 -4 4 h-48 a4 4 0 0 1 -4 -4 z" fill="#fff" stroke={tint} strokeWidth="3" strokeLinejoin="round" />
          <rect x="40" y="30" width="26" height="22" rx="2" fill="#fff" stroke={tint} strokeWidth="2.5" opacity="0.8" />
          <path d="M20 52 h60 l-6 26 h-52 z" fill={tint} opacity="0.92" />
          <line x1="46" y1="38" x2="60" y2="38" stroke={tint} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          <line x1="46" y1="44" x2="56" y2="44" stroke={tint} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        </>
      )}
    </svg>
  );
}

export default function BulkStatusButton({
  selectedIds,
  doctype,
  onDone,
  className = "mr-2",
  noun = "notice",
}) {
  const [pending, setPending] = useState(null); // status awaiting confirmation
  const [saving, setSaving] = useState(false);

  const ids = (selectedIds || []).filter(Boolean);
  const count = ids.length;
  const resolvedDoctype = typeof doctype === "function" ? doctype() : doctype;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await postData(NOTICES.BULK_UPDATE_STATUS, {
        doctype: resolvedDoctype,
        docnames: ids,
        notice_status: pending,
      });
      if (res?.success === false) {
        toast.error(res?.message || "Failed to update status");
        return;
      }
      const updated = res?.data?.updated ?? count;
      const skipped = res?.data?.skipped?.length ?? 0;
      if (updated > 0) {
        toast.info(
          `Marked ${updated} ${noun}${updated === 1 ? "" : "s"} as ${pending}` +
            (skipped ? `; ${skipped} could not be updated` : ""),
        );
      } else {
        toast.warning(
          res?.message || `No ${noun}s were updated`,
        );
      }
      setPending(null);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.message || e || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const menu = {
    items: STATUSES.map((s) => ({ key: s.key, label: s.label, icon: s.icon })),
    onClick: ({ key }) => setPending(key),
  };

  const button = (
    <Dropdown menu={menu} disabled={count === 0} trigger={["click"]}>
      <Button type="primary" className={className} disabled={count === 0}>
        Change Status <DownOutlined />
      </Button>
    </Dropdown>
  );

  return (
    <>
      {count === 0 ? (
        <Tooltip title={`Please select a ${noun}`}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Modal
        title=""
        open={pending !== null}
        onOk={handleConfirm}
        onCancel={() => !saving && setPending(null)}
        okText={`Mark ${count} as ${pending}`}
        okButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        centered
        zIndex={9999}
      >
        <div className="flex items-center" style={{ gap: 20, paddingTop: 20, paddingRight: 8 }}>
          <StatusArt status={pending} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
              {`Mark ${count} selected ${noun}${count === 1 ? "" : "s"} as ${pending}?`}
            </p>
            <p style={{ color: "#6b7280", margin: 0 }}>
              You can change it back at any time.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}
